import { useMemo, useRef, useState, type ReactNode } from 'react'
import { View, Text } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { useTranslation } from 'react-i18next'
import {
  MapPin,
  Clock,
  Luggage,
  Minus,
  Plus,
  BadgeCheck,
  ShieldAlert,
  IdCard,
  Globe,
  Info,
  AlertTriangle,
  Check,
} from '@/components/ui/icons'
import { Image } from '@/components/ui/Image'
import { PhotoLightbox, usePhotoLightbox } from '@/components/ui/PhotoLightbox'
import { Pressable } from '@/components/ui/Pressable'
import { BackButton } from '@/components/ui/BackButton'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Checkbox } from '@/components/ui/Checkbox'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { DatePicker } from '@/components/ui/DatePicker'
import { TimeSlotPicker } from '@/components/ui/TimeSlotPicker'
import { CountryFlag } from '@/components/ui/CountryFlag'
import { ToursStars } from '@/components/tours/ToursStars'
import { TourPhotoGallery } from '@/components/tours/TourPhotoGallery'
import { TourRouteMap } from '@/components/tours/TourRouteMap'
import { TourStopList } from '@/components/tours/TourStopList'
import { TourStartPointField } from '@/components/tours/TourStartPointField'
import type { SearchLocation } from '@/components/location/LocationSearchModal'
import { useTheme } from '@/lib/hooks/use-theme'
import { useSemanticColors, INTERACTION, Z_INDEX } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { COLORS, FONTS, SPACING, RADIUS } from '@/lib/brand'
import { tourDetailCoverAspect, tourDetailCoverMaxHeight } from '@/lib/tours/catalog-layout'
import { tourMapCanvasHeight } from '@/lib/maps/tour-map-layout'
import { GENERIC_IMAGE_BLURHASH } from '@/lib/utils/blurhash'
import { formatBookingDateTime } from '@/lib/utils/dates'
import { formatPrice } from '@/lib/utils/format'
import { MIN_TOUR_PASSENGERS } from '@/lib/tours/booking-rules'
import type {
  TourExperienceDetail,
  TourLuggageCode,
  TourReview,
  TourVehicleOption,
} from '@/lib/types/tours.types'

const LUGGAGE_OPTIONS: ReadonlyArray<{ code: TourLuggageCode }> = [
  { code: 'light' },
  { code: 'beach' },
  { code: 'luggage' },
  { code: 'sports' },
]

/**
 * Mapbox returns useful full addresses, but repeating one in the first screen
 * of a private quote buries the actual decision. The complete label remains in
 * the stop list below; this is intentionally just the route's scan-friendly
 * place name.
 */
function conciseStopLabel(label: string | undefined): string {
  const trimmed = label?.trim() ?? ''
  if (!trimmed) return ''
  return trimmed.split(',')[0]?.trim() || trimmed
}

export interface TourExperienceDetailViewProps {
  experience: TourExperienceDetail
  editorial: boolean
  twoColumn: boolean
  padX: number
  catalogFallback: string
  routeLabel: string
  durationLabel: string
  descriptionBody: string
  destinationName: string | null
  selectedVehicle: TourVehicleOption | null
  vehicles: TourVehicleOption[]
  date: string | null
  time: string | null
  earliestBookableDate: string
  passengerCount: number
  maxPassengers: number
  luggageCodes: TourLuggageCode[]
  /** Punto de recogida elegido; `null` = punto de encuentro oficial del tour. */
  startPoint: SearchLocation | null
  termsAccepted: boolean
  consentError: string | null
  reserveErrorKey: string | null
  perPersonCents: number
  isReserveDisabled: boolean
  reservePending: boolean
  isAuthenticated: boolean
  contactRequirement: 'phone' | 'email' | null
  reviewsLoading: boolean
  reviews: TourReview[] | undefined
  onBack: () => void
  onDateChange: (value: string | null) => void
  onTimeChange: (value: string | null) => void
  onPassengerCount: (next: number) => void
  onSelectVehicle: (unitId: string) => void
  onToggleLuggage: (code: TourLuggageCode) => void
  onStartPointChange: (value: SearchLocation | null) => void
  onTermsChange: (checked: boolean) => void
  onReserve: () => void
  onVerify: () => void
  onCreateAnotherQuote?: () => void
}

export function TourExperienceDetailView({
  experience,
  editorial,
  twoColumn,
  padX,
  catalogFallback,
  routeLabel,
  durationLabel,
  descriptionBody,
  destinationName,
  selectedVehicle,
  vehicles,
  date,
  time,
  earliestBookableDate,
  passengerCount,
  maxPassengers,
  luggageCodes,
  startPoint,
  termsAccepted,
  consentError,
  reserveErrorKey,
  perPersonCents,
  isReserveDisabled,
  reservePending,
  isAuthenticated,
  contactRequirement,
  reviewsLoading,
  reviews,
  onBack,
  onDateChange,
  onTimeChange,
  onPassengerCount,
  onSelectVehicle,
  onToggleLuggage,
  onStartPointChange,
  onTermsChange,
  onReserve,
  onVerify,
  onCreateAnotherQuote,
}: TourExperienceDetailViewProps) {
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const lightbox = usePhotoLightbox()
  const travelInfo = experience.travelInfo
  const customQuote = experience.customQuote ?? null
  const customQuoteExpired =
    Boolean(customQuote?.isExpired) ||
    (customQuote !== null && new Date(customQuote.expiresAt).getTime() <= Date.now())
  const galleryPhotos = travelInfo?.gallery
  const lightboxImages = useMemo(() => {
    if (galleryPhotos?.length) {
      return galleryPhotos.map((photo) => ({
        id: photo.id,
        uri: photo.imageUrl,
        blurhash: GENERIC_IMAGE_BLURHASH,
        accessibilityLabel: photo.alt,
      }))
    }
    return experience.coverImageUrl
      ? [
          {
            id: 'tours-cover',
            uri: experience.coverImageUrl,
            blurhash: GENERIC_IMAGE_BLURHASH,
            accessibilityLabel: t('tours.cover_alt', { title: experience.title }),
          },
        ]
      : []
  }, [experience.coverImageUrl, experience.title, galleryPhotos, t])
  const coverLightboxIndex = Math.max(0, galleryPhotos?.findIndex((photo) => photo.isCover) ?? -1)
  const showConsent = Boolean(travelInfo?.meetingPoint)
  const coverAspect = tourDetailCoverAspect(editorial)
  const coverMaxHeight = tourDetailCoverMaxHeight(editorial)
  const mapHeight = tourMapCanvasHeight({ editorial, twoColumn })
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null)
  const mobileScrollRef = useRef<ScrollView>(null)
  const [consentOffsetY, setConsentOffsetY] = useState(0)

  // The booking rail stays visible on phones while travel consent is part of
  // the scrollable itinerary. Leaving a required consent error below the fold
  // makes an otherwise active "Reservar" CTA look inert. Keep the error next
  // to its checkbox (the only actionable place), and take the person there on
  // the same tap instead of duplicating competing warnings in the footer.
  const handleReservePress = () => {
    const needsConsent = showConsent && !termsAccepted
    onReserve()
    if (!needsConsent || consentOffsetY <= 0) return

    requestAnimationFrame(() => {
      mobileScrollRef.current?.scrollTo({
        y: Math.max(0, consentOffsetY - SPACING.xl),
        animated: true,
      })
    })
  }

  const header = (
    <ScreenHeader
      // The hero owns the experience name. Keeping the same long title in
      // the navigation row created a duplicated heading before any trip
      // information and pushed the first decision farther down the screen.
      // `Tours` preserves a clear, aligned back context without competing
      // with the destination hierarchy below.
      title={editorial ? undefined : t('tabs.tours')}
      titleNumberOfLines={1}
      titleTestID={editorial ? undefined : 'tours.detail-navigation-title'}
      backTestID="tours.detail-back"
      onBackPress={onBack}
      fallbackHref={catalogFallback}
      style={{ paddingHorizontal: 0 }}
    />
  )

  const cover = customQuote ? null : (
    <View testID="tours.detail-cover">
      <View
        style={{
          width: '100%',
          aspectRatio: coverAspect,
          maxHeight: coverMaxHeight,
          borderRadius: editorial ? RADIUS.xl : RADIUS.lg,
          overflow: 'hidden',
          backgroundColor: colors.surfaceSubtle,
        }}
      >
        {experience.coverImageUrl ? (
          <Pressable
            onPress={() => lightbox.open(lightboxImages, coverLightboxIndex)}
            accessibilityRole="button"
            accessibilityLabel={t('tours.cover_alt', { title: experience.title })}
            testID="tours.detail-cover-photo"
            style={{ width: '100%', height: '100%' }}
          >
            <Image
              source={experience.coverImageUrl}
              blurhash={GENERIC_IMAGE_BLURHASH}
              width="100%"
              height="100%"
              contentFit="cover"
              accessibilityLabel={t('tours.cover_alt', { title: experience.title })}
            />
          </Pressable>
        ) : (
          <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
            <MapPin size={28} color={colors.textMuted} strokeWidth={1.5} />
          </View>
        )}
        {editorial ? (
          <View
            testID="tours.detail-hero-chrome"
            pointerEvents="box-none"
            style={{
              position: 'absolute',
              top: SPACING.sm,
              left: SPACING.sm,
              zIndex: Z_INDEX.raised,
            }}
          >
            <BackButton
              onPress={onBack}
              fallbackHref={catalogFallback}
              preferFallback
              testID="tours.detail-back"
            />
          </View>
        ) : null}
        {destinationName && experience.destinationCountryCode ? (
          <View
            style={{
              position: 'absolute',
              left: SPACING.md,
              bottom: SPACING.md,
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.xs,
              paddingHorizontal: SPACING.md,
              paddingVertical: 4,
              borderRadius: RADIUS.full,
              backgroundColor: colors.scrim,
            }}
          >
            <CountryFlag iso2={experience.destinationCountryCode} width={18} />
            <Text
              style={{ fontFamily: FONTS.body, fontSize: 13, color: COLORS.white }}
              testID="tours.detail-destination"
            >
              {destinationName}
            </Text>
          </View>
        ) : null}
      </View>
    </View>
  )

  const identity = customQuote ? null : (
    <View style={{ marginTop: editorial ? 0 : SPACING.lg }}>
      <>
        <Text
          testID="tours.detail-title"
          style={{
            fontFamily: FONTS.bodyBold,
            fontSize: editorial && twoColumn ? 28 : 24,
            lineHeight: editorial && twoColumn ? 34 : 30,
            color: colors.textPrimary,
            letterSpacing: -0.3,
          }}
        >
          {experience.title}
        </Text>
        <Text
          style={{
            marginTop: 2,
            fontFamily: FONTS.body,
            fontSize: 15,
            color: colors.textSecondary,
          }}
        >
          {routeLabel}
        </Text>
      </>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: SPACING.xs }}>
        <BadgeCheck size={14} color={colors.success} strokeWidth={1.8} />
        <Text
          style={{
            marginLeft: 4,
            fontFamily: FONTS.body,
            fontSize: 13,
            color: colors.textSecondary,
          }}
        >
          {experience.host.displayName} · {t('tours.host_verified')}
        </Text>
      </View>
      {experience.ratingCount && experience.ratingCount > 0 ? (
        <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: SPACING.xs }}>
          <ToursStars value={experience.ratingAvg ?? 0} size={14} />
          <Text
            style={{
              marginLeft: 6,
              fontFamily: FONTS.body,
              fontSize: 13,
              color: colors.textSecondary,
            }}
            testID="tours.detail-rating"
          >
            {experience.ratingAvg?.toFixed(1)} ·{' '}
            {t('tours.reviews.rating_count', { count: experience.ratingCount })}
          </Text>
        </View>
      ) : null}
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          marginTop: SPACING.md,
          gap: SPACING.sm,
        }}
      >
        <MetaChip
          icon={<Clock size={14} color={colors.textMuted} strokeWidth={1.8} />}
          label={durationLabel}
        />
        {experience.distanceKm ? (
          <MetaChip
            icon={<MapPin size={14} color={colors.textMuted} strokeWidth={1.8} />}
            label={`${experience.distanceKm} km`}
          />
        ) : null}
      </View>
    </View>
  )

  const internationalBanner = travelInfo?.isInternational ? (
    <View
      style={{
        marginTop: SPACING.md,
        flexDirection: 'row',
        alignItems: 'center',
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: colors.warning,
        backgroundColor: colors.surfaceSubtle,
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.sm + 2,
      }}
      testID="tours.detail-international-banner"
    >
      <Globe size={18} color={colors.warning} strokeWidth={1.8} />
      <Text
        style={{
          marginLeft: SPACING.sm,
          flex: 1,
          fontFamily: FONTS.bodyBold,
          fontSize: 13,
          color: colors.warning,
        }}
      >
        {t('tours.travel_info.international_title')}
      </Text>
    </View>
  ) : null

  const description = customQuote ? null : (
    <Text
      style={{
        marginTop: SPACING.lg,
        fontFamily: FONTS.body,
        fontSize: 15,
        lineHeight: 22,
        color: colors.textSecondary,
      }}
      testID="tours.detail-description"
    >
      {descriptionBody}
    </Text>
  )

  const photoGallery =
    !customQuote && galleryPhotos?.length ? (
      <TourPhotoGallery
        photos={galleryPhotos}
        onPhotoPress={(index) => lightbox.open(lightboxImages, index)}
      />
    ) : null

  const routeMap =
    experience.stops.length >= 2 ? (
      <View style={{ marginTop: customQuote ? SPACING.lg : SPACING.xl }}>
        <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
          {t('tours.stops')}
        </Text>
        <View style={{ marginTop: SPACING.sm }}>
          <TourRouteMap
            stops={experience.stops}
            latestLocation={null}
            routeGeojson={experience.routeGeojson}
            canvasHeight={customQuote ? Math.min(mapHeight, 224) : mapHeight}
            selectedStopId={selectedStopId}
            onStopPress={setSelectedStopId}
            testID="tours.detail-route-map"
          />
        </View>
        <TourStopList
          stops={experience.stops}
          selectedStopId={selectedStopId}
          onStopPress={setSelectedStopId}
          testID="tours.detail-stop-list"
        />
      </View>
    ) : null

  const customQuoteHero = customQuote ? (
    <View
      testID="tours.custom-quote-hero"
      style={{
        marginTop: SPACING.sm,
        borderRadius: RADIUS.xl,
        borderWidth: 1,
        borderColor: customQuoteExpired ? colors.border : 'rgba(91,84,144,0.22)',
        backgroundColor: customQuoteExpired
          ? colors.surfaceSubtle
          : isDark
            ? 'rgba(91,84,144,0.16)'
            : 'rgba(91,84,144,0.055)',
        padding: SPACING.lg,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.xs }}>
        <BadgeCheck
          size={18}
          color={customQuoteExpired ? colors.textMuted : COLORS.purple.DEFAULT}
          strokeWidth={2}
        />
        <Text
          style={{ flex: 1, fontFamily: FONTS.bodyBold, fontSize: 13, color: colors.textPrimary }}
        >
          {t('tours.custom_quote.detail_eyebrow')}
        </Text>
      </View>
      <Text
        style={{
          marginTop: SPACING.sm,
          fontFamily: FONTS.bodyBold,
          fontSize: 22,
          lineHeight: 28,
          letterSpacing: -0.25,
          color: colors.textPrimary,
        }}
      >
        {t('tours.custom_quote.detail_title')}
      </Text>
      <Text
        style={{
          marginTop: 2,
          fontFamily: FONTS.body,
          fontSize: 14,
          lineHeight: 20,
          color: colors.textSecondary,
        }}
      >
        {t('tours.custom_quote.detail_subtitle')}
      </Text>
      <View
        testID="tours.custom-quote-route"
        style={{
          marginTop: SPACING.md,
          borderRadius: RADIUS.lg,
          backgroundColor: customQuoteExpired
            ? colors.surface
            : isDark
              ? 'rgba(255,255,255,0.07)'
              : 'rgba(255,255,255,0.72)',
          paddingHorizontal: SPACING.md,
          paddingVertical: SPACING.sm + 2,
          gap: SPACING.sm,
        }}
      >
        <PrivateRoutePlace
          label={t('tours.custom_quote.origin')}
          place={conciseStopLabel(experience.stops[0]?.label)}
          color={COLORS.purple.DEFAULT}
        />
        <PrivateRoutePlace
          label={t('tours.custom_quote.destination')}
          place={conciseStopLabel(experience.stops[experience.stops.length - 1]?.label)}
          color={COLORS.coral.DEFAULT}
        />
      </View>
      <View
        style={{ marginTop: SPACING.md, flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.xs }}
      >
        <View
          style={{
            borderRadius: RADIUS.full,
            backgroundColor: colors.surface,
            paddingHorizontal: SPACING.sm,
            paddingVertical: 4,
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textPrimary }}>
            {passengerCount} {t('tours.trip.people_short')}
          </Text>
        </View>
        {selectedVehicle ? (
          <View
            style={{
              borderRadius: RADIUS.full,
              backgroundColor: colors.surface,
              paddingHorizontal: SPACING.sm,
              paddingVertical: 4,
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textPrimary }}>
              {selectedVehicle.label}
            </Text>
          </View>
        ) : null}
        <View
          style={{
            borderRadius: RADIUS.full,
            backgroundColor: colors.surface,
            paddingHorizontal: SPACING.sm,
            paddingVertical: 4,
          }}
        >
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textPrimary }}>
            {durationLabel}
          </Text>
        </View>
      </View>
      <Text
        style={{
          marginTop: SPACING.sm,
          fontFamily: FONTS.body,
          fontSize: 12,
          color: customQuoteExpired ? colors.textError : colors.textMuted,
        }}
      >
        {customQuoteExpired
          ? t('tours.custom_quote.expired_title')
          : t('tours.custom_quote.expires_at', {
              time: formatBookingDateTime(customQuote.expiresAt, i18n.language),
            })}
      </Text>
    </View>
  ) : null

  const bookingFields = (
    <View>
      {/* Un tour privado cotizado ya nació con su origen elegido; para el
          catálogo, este es el momento de decidir dónde recoge el conductor. */}
      {!customQuote ? (
        <View style={{ marginBottom: SPACING.xl }}>
          <TourStartPointField
            meetingPoint={travelInfo?.meetingPoint}
            originLabel={experience.originLabel}
            value={startPoint}
            onChange={onStartPointChange}
          />
        </View>
      ) : null}
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
        {t('tours.choose_date')}
      </Text>
      <View style={{ marginTop: SPACING.sm }}>
        <DatePicker
          value={date}
          onChange={onDateChange}
          label={t('tours.date')}
          minDate={earliestBookableDate}
          testID="tours.date-picker"
        />
      </View>
      <View style={{ marginTop: SPACING.md }}>
        <TimeSlotPicker
          value={time}
          onChange={onTimeChange}
          intervalMinutes={60}
          label={t('tours.time')}
          testID="tours.time-picker"
        />
      </View>
      <Text
        style={{
          marginTop: SPACING.xs,
          fontFamily: FONTS.body,
          fontSize: 12,
          color: colors.textMuted,
        }}
        testID="tours.minimum-lead-time-hint"
      >
        {t('tours.minimum_lead_time_hint')}
      </Text>

      {!customQuote ? (
        <>
          <Text
            style={{
              marginTop: SPACING.xl,
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              color: colors.textPrimary,
            }}
          >
            {t('tours.passengers')}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: SPACING.sm }}>
            <Pressable
              onPress={() => onPassengerCount(Math.max(MIN_TOUR_PASSENGERS, passengerCount - 1))}
              disabled={passengerCount <= MIN_TOUR_PASSENGERS}
              accessibilityRole="button"
              accessibilityLabel={t('tours.decrease_passengers')}
              testID="tours.passenger-minus"
              style={{
                width: INTERACTION.minTouchTarget,
                height: INTERACTION.minTouchTarget,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: RADIUS.full,
                borderWidth: 1,
                borderColor: colors.border,
                backgroundColor: colors.surface,
              }}
            >
              <Minus
                size={18}
                color={
                  passengerCount <= MIN_TOUR_PASSENGERS ? colors.textDisabled : colors.textPrimary
                }
                strokeWidth={2}
              />
            </Pressable>
            <Text
              style={{
                minWidth: 64,
                textAlign: 'center',
                fontFamily: FONTS.bodyBold,
                fontSize: 17,
                color: colors.textPrimary,
              }}
              testID="tours.passenger-count"
            >
              {passengerCount}
            </Text>
            <Pressable
              onPress={() => onPassengerCount(Math.min(maxPassengers, passengerCount + 1))}
              disabled={passengerCount >= maxPassengers}
              accessibilityRole="button"
              accessibilityLabel={t('tours.increase_passengers')}
              testID="tours.passenger-plus"
              style={{
                width: INTERACTION.minTouchTarget,
                height: INTERACTION.minTouchTarget,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: RADIUS.full,
                borderWidth: 1,
                borderColor: colors.border,
                backgroundColor: colors.surface,
              }}
            >
              <Plus
                size={18}
                color={passengerCount >= maxPassengers ? colors.textDisabled : colors.textPrimary}
                strokeWidth={2}
              />
            </Pressable>
          </View>
          <Text
            style={{
              marginTop: SPACING.xs,
              fontFamily: FONTS.body,
              fontSize: 13,
              color: colors.textMuted,
            }}
          >
            {t('tours.passenger_range', {
              min: MIN_TOUR_PASSENGERS,
              max: maxPassengers,
            })}
          </Text>

          <Text
            style={{
              marginTop: SPACING.xl,
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              color: colors.textPrimary,
            }}
          >
            {t('tours.choose_vehicle')}
          </Text>
          <Text
            style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 13, color: colors.textMuted }}
          >
            {t('tours.choose_vehicle_hint')}
          </Text>
          <View accessibilityRole="radiogroup" style={{ marginTop: SPACING.sm }}>
            {vehicles.map((vehicle) => (
              <VehicleOption
                key={vehicle.unitId}
                vehicle={vehicle}
                selected={selectedVehicle?.unitId === vehicle.unitId}
                disabled={passengerCount > vehicle.seats}
                onSelect={() => onSelectVehicle(vehicle.unitId)}
              />
            ))}
          </View>
        </>
      ) : null}

      <Text
        style={{
          marginTop: SPACING.xl,
          fontFamily: FONTS.bodyBold,
          fontSize: 17,
          color: colors.textPrimary,
        }}
      >
        {t('tours.luggage')}
      </Text>
      <Text style={{ marginTop: 2, fontFamily: FONTS.body, fontSize: 13, color: colors.textMuted }}>
        {t('tours.luggage_hint')}
      </Text>
      <View
        style={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          marginTop: SPACING.sm,
          gap: SPACING.sm,
        }}
      >
        {LUGGAGE_OPTIONS.map(({ code }) => (
          <LuggageChip
            key={code}
            code={code}
            selected={luggageCodes.includes(code)}
            onToggle={() => onToggleLuggage(code)}
          />
        ))}
      </View>
    </View>
  )

  const travelCards = travelInfo?.meetingPoint ? (
    <View style={{ marginTop: SPACING.xl }}>
      <Text
        style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}
        testID="tours.detail-travel-info-title"
      >
        {t('tours.travel_info.title')}
      </Text>
      <View style={{ marginTop: SPACING.sm, gap: SPACING.sm }}>
        <TravelInfoCard
          icon={<MapPin size={15} color={colors.textAccent} strokeWidth={1.8} />}
          title={t('tours.travel_info.meeting_point')}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 14,
              lineHeight: 20,
              color: colors.textPrimary,
            }}
          >
            {travelInfo.meetingPoint.label}
          </Text>
          {travelInfo.meetingPoint.address ? (
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 20,
                color: colors.textSecondary,
              }}
            >
              {travelInfo.meetingPoint.address}
            </Text>
          ) : null}
          {travelInfo.meetingPoint.note ? (
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 18,
                color: colors.textMuted,
              }}
            >
              {travelInfo.meetingPoint.note}
            </Text>
          ) : null}
        </TravelInfoCard>

        {travelInfo.whatToBring?.length ? (
          <TravelInfoCard
            icon={<Luggage size={15} color={colors.textAccent} strokeWidth={1.8} />}
            title={t('tours.travel_info.what_to_bring')}
          >
            <BulletList items={travelInfo.whatToBring} />
          </TravelInfoCard>
        ) : null}

        {travelInfo.documents?.required?.length ? (
          <TravelInfoCard
            icon={<IdCard size={15} color={colors.warning} strokeWidth={1.8} />}
            title={t('tours.travel_info.documents')}
          >
            <BulletList items={travelInfo.documents.required} marker="warn" />
            {travelInfo.documents.recommended?.length ? (
              <View style={{ marginTop: SPACING.sm }}>
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textMuted }}>
                  {t('tours.travel_info.documents_recommended')}
                </Text>
                <View style={{ marginTop: SPACING.xs }}>
                  <BulletList items={travelInfo.documents.recommended} />
                </View>
              </View>
            ) : null}
            {travelInfo.documents.note ? (
              <Text
                style={{
                  marginTop: SPACING.sm,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  lineHeight: 18,
                  color: colors.textSecondary,
                }}
              >
                {travelInfo.documents.note}
              </Text>
            ) : null}
          </TravelInfoCard>
        ) : null}

        {travelInfo.border?.crossings?.length ? (
          <TravelInfoCard
            icon={<Globe size={15} color={colors.warning} strokeWidth={1.8} />}
            title={t('tours.travel_info.border_crossing')}
          >
            <BulletList items={travelInfo.border.crossings} marker="warn" />
            {travelInfo.border.note ? (
              <Text
                style={{
                  marginTop: SPACING.sm,
                  fontFamily: FONTS.body,
                  fontSize: 13,
                  lineHeight: 18,
                  color: colors.textSecondary,
                }}
              >
                {travelInfo.border.note}
              </Text>
            ) : null}
            {travelInfo.border.currencyNote ? (
              <Text
                style={{
                  marginTop: SPACING.xs,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 13,
                  lineHeight: 18,
                  color: colors.textPrimary,
                }}
              >
                {travelInfo.border.currencyNote}
              </Text>
            ) : null}
          </TravelInfoCard>
        ) : null}

        {travelInfo.policies?.length ? (
          <TravelInfoCard
            icon={<Info size={15} color={colors.textAccent} strokeWidth={1.8} />}
            title={t('tours.travel_info.policies')}
          >
            <BulletList items={travelInfo.policies} />
          </TravelInfoCard>
        ) : null}

        {travelInfo.legalNotice ? (
          <TravelInfoCard
            icon={<AlertTriangle size={15} color={colors.textMuted} strokeWidth={1.8} />}
            title={t('tours.travel_info.legal_notice')}
          >
            <Text
              style={{
                fontFamily: FONTS.body,
                fontSize: 14,
                lineHeight: 20,
                color: colors.textSecondary,
              }}
            >
              {travelInfo.legalNotice}
            </Text>
          </TravelInfoCard>
        ) : null}
      </View>
    </View>
  ) : null

  const consent = showConsent ? (
    <View
      style={{ marginTop: SPACING.md }}
      onLayout={(event) => setConsentOffsetY(event.nativeEvent.layout.y)}
      testID="tours.travel-consent"
    >
      <Checkbox
        checked={termsAccepted}
        onChange={onTermsChange}
        required
        error={consentError ?? undefined}
        label={
          <Text
            style={{
              fontFamily: FONTS.body,
              fontSize: 14,
              lineHeight: 20,
              color: colors.textSecondary,
            }}
          >
            {travelInfo?.isInternational
              ? t('tours.travel_info.consent_label_international')
              : t('tours.travel_info.consent_label')}
          </Text>
        }
        accessibilityLabel={
          travelInfo?.isInternational
            ? t('tours.travel_info.consent_label_international')
            : t('tours.travel_info.consent_label')
        }
        testID="tours.terms-consent"
      />
    </View>
  ) : null

  const requirements = contactRequirement ? (
    <View style={{ marginTop: SPACING.xl }}>
      <Card>
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <ShieldAlert size={18} color={colors.warning} strokeWidth={1.8} />
          <Text
            style={{
              marginLeft: SPACING.sm,
              flex: 1,
              fontFamily: FONTS.bodyBold,
              fontSize: 15,
              color: colors.textPrimary,
            }}
          >
            {t('tours.requirements.title')}
          </Text>
        </View>
        <Text
          style={{
            marginTop: SPACING.xs,
            fontFamily: FONTS.body,
            fontSize: 14,
            lineHeight: 20,
            color: colors.textSecondary,
          }}
        >
          {contactRequirement === 'phone'
            ? t('tours.requirements.phone')
            : t('tours.requirements.email')}
        </Text>
      </Card>
    </View>
  ) : null

  const reviewsBlock = customQuote ? null : (
    <View style={{ marginTop: SPACING.xl }}>
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
        {t('tours.reviews.title')}
      </Text>
      {reviewsLoading ? (
        <View style={{ marginTop: SPACING.sm, gap: SPACING.sm }}>
          <Skeleton width="100%" height={64} borderRadius={RADIUS.lg} staggerIndex={0} />
          <Skeleton width="100%" height={64} borderRadius={RADIUS.lg} staggerIndex={1} />
        </View>
      ) : reviews && reviews.length > 0 ? (
        <View style={{ marginTop: SPACING.sm, gap: SPACING.sm }}>
          {reviews.slice(0, 5).map((review) => (
            <Card key={review.id}>
              <ToursStars value={review.rating} size={14} />
              {review.comment ? (
                <Text
                  style={{
                    marginTop: SPACING.xs,
                    fontFamily: FONTS.body,
                    fontSize: 14,
                    lineHeight: 20,
                    color: colors.textSecondary,
                  }}
                >
                  {review.comment}
                </Text>
              ) : null}
            </Card>
          ))}
        </View>
      ) : (
        <Text
          style={{
            marginTop: SPACING.sm,
            fontFamily: FONTS.body,
            fontSize: 14,
            color: colors.textMuted,
          }}
        >
          {t('tours.reviews.no_reviews_yet')}
        </Text>
      )}
    </View>
  )

  const reserveFooter = customQuoteExpired ? (
    <View testID="tours.custom-quote-expired">
      <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
        {t('tours.custom_quote.expired_title')}
      </Text>
      <Text
        style={{
          marginTop: SPACING.xs,
          fontFamily: FONTS.body,
          fontSize: 13,
          lineHeight: 19,
          color: colors.textSecondary,
        }}
      >
        {t('tours.custom_quote.expired_body')}
      </Text>
      <View style={{ marginTop: SPACING.md }}>
        <Button
          title={t('tours.custom_quote.new_quote')}
          size="md"
          onPress={onCreateAnotherQuote ?? onBack}
          testID="tours.custom-quote-new"
        />
      </View>
    </View>
  ) : (
    <View>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          marginBottom: SPACING.xs,
        }}
      >
        <View>
          <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textMuted }}>
            {t('tours.per_person')}
          </Text>
          <Text
            style={{ fontFamily: FONTS.bodyBold, fontSize: 22, color: colors.textAccent }}
            testID="tours.detail-per-person"
          >
            {formatPrice(perPersonCents)}
          </Text>
        </View>
      </View>

      {reserveErrorKey ? (
        <Text
          style={{
            marginTop: SPACING.md,
            fontFamily: FONTS.body,
            fontSize: 13,
            color: colors.textError,
          }}
          testID="tours.reserve-error"
        >
          {t(reserveErrorKey)}
        </Text>
      ) : null}

      <View style={{ marginTop: SPACING.md }}>
        <Button
          title={
            !isAuthenticated
              ? t('auth.login')
              : contactRequirement
                ? t('tours.requirements.cta')
                : t('tours.reserve')
          }
          size="md"
          loading={reservePending}
          disabled={isAuthenticated && isReserveDisabled && !contactRequirement}
          onPress={!isAuthenticated || contactRequirement ? onVerify : handleReservePress}
          testID="tours.reserve-button"
        />
      </View>
      {!isAuthenticated ? (
        <Text
          style={{
            marginTop: SPACING.sm,
            textAlign: 'center',
            fontFamily: FONTS.body,
            fontSize: 12,
            color: colors.textMuted,
          }}
          testID="tours.reserve-sign-in-hint"
        >
          {t('tours.reserve_sign_in_hint')}
        </Text>
      ) : (
        <Text
          style={{
            marginTop: SPACING.sm,
            textAlign: 'center',
            fontFamily: FONTS.body,
            fontSize: 12,
            color: colors.textMuted,
          }}
          testID="tours.reserve-hint"
        >
          {t('tours.reserve_hint')}
        </Text>
      )}
    </View>
  )

  const story = (
    <View>
      {customQuoteHero}
      {identity}
      {internationalBanner}
      {description}
      {photoGallery}
      {routeMap}
      {travelCards}
      {requirements}
      {reviewsBlock}
    </View>
  )

  if (editorial) {
    return (
      <ScreenContainer variant="full-bleed" scroll={false}>
        <View style={{ flex: 1 }} testID="tours.detail-canvas">
          <ScrollView
            style={{ flex: 1 }}
            contentContainerStyle={{ paddingBottom: SPACING['3xl'] }}
            showsVerticalScrollIndicator={false}
          >
            {customQuote ? (
              <View style={{ paddingHorizontal: padX, paddingTop: SPACING.sm }}>{header}</View>
            ) : null}
            {cover ? (
              <View style={{ paddingHorizontal: padX, paddingTop: SPACING.sm }}>{cover}</View>
            ) : null}
            <View
              style={{
                flexDirection: twoColumn ? 'row' : 'column',
                alignItems: 'flex-start',
                paddingHorizontal: padX,
                marginTop: customQuote ? SPACING.sm : SPACING.md,
                gap: SPACING.xl,
              }}
            >
              <View style={{ flex: twoColumn ? 3 : undefined, width: '100%', minWidth: 0 }}>
                {story}
              </View>
              <View
                style={{
                  flex: twoColumn ? 2 : undefined,
                  width: '100%',
                  maxWidth: twoColumn ? 400 : undefined,
                  minWidth: twoColumn ? 320 : 0,
                }}
              >
                <View
                  testID="tours.detail-booking-rail"
                  style={{
                    position: twoColumn ? ('sticky' as 'relative') : 'relative',
                    top: twoColumn ? SPACING.lg : 0,
                    backgroundColor: colors.surface,
                    borderRadius: RADIUS.xl,
                    padding: SPACING.lg,
                    borderWidth: 1,
                    borderColor: colors.border,
                    ...WEB_SHADOWS.md,
                  }}
                >
                  {bookingFields}
                  {consent}
                  <View style={{ marginTop: SPACING.lg }}>{reserveFooter}</View>
                </View>
              </View>
            </View>
          </ScrollView>
        </View>
        <PhotoLightbox
          visible={lightbox.visible}
          images={lightbox.images}
          index={lightbox.index}
          onIndexChange={lightbox.setIndex}
          onClose={lightbox.close}
        />
      </ScreenContainer>
    )
  }

  return (
    <ScreenContainer scroll={false}>
      <View style={{ flex: 1 }}>
        <ScrollView
          ref={mobileScrollRef}
          style={{ flex: 1 }}
          contentContainerStyle={{ paddingHorizontal: SPACING.lg, paddingBottom: SPACING.lg }}
          showsVerticalScrollIndicator={false}
        >
          {header}
          {cover}
          {customQuoteHero}
          {identity}
          {internationalBanner}
          {description}
          {/* En móvil la decisión va primero: la fecha, el punto de recogida y
              el grupo no pueden quedar enterrados tras galería, mapa y paradas
              (y una pantalla que se decide sin verla no es intuitiva). La
              galería y el recorrido siguen abajo para quien quiera explorar. */}
          <View style={{ marginTop: SPACING.xl }}>{bookingFields}</View>
          {photoGallery}
          {routeMap}
          {travelCards}
          {consent}
          {requirements}
          {reviewsBlock}
        </ScrollView>
        <View
          style={{
            paddingHorizontal: SPACING.lg,
            paddingTop: SPACING.sm,
            paddingBottom: SPACING.md,
            borderTopWidth: 1,
            borderTopColor: colors.border,
            backgroundColor: colors.surface,
          }}
        >
          {reserveFooter}
        </View>
      </View>
      <PhotoLightbox
        visible={lightbox.visible}
        images={lightbox.images}
        index={lightbox.index}
        onIndexChange={lightbox.setIndex}
        onClose={lightbox.close}
      />
    </ScreenContainer>
  )
}

function LuggageChip({
  code,
  selected,
  onToggle,
}: {
  code: TourLuggageCode
  selected: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <Pressable
      onPress={onToggle}
      accessibilityRole="checkbox"
      accessibilityState={{ checked: selected }}
      accessibilityLabel={t(`tours.luggage_${code}`)}
      testID={`tours.luggage-${code}`}
      style={{
        minHeight: INTERACTION.minTouchTarget,
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.xs + 2,
        borderRadius: RADIUS.full,
        borderWidth: 1,
        borderColor: selected ? colors.textAccent : colors.border,
        backgroundColor: selected ? colors.surfaceSubtle : colors.surface,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Text
        style={{
          fontFamily: FONTS.body,
          fontSize: 13,
          color: selected ? colors.textPrimary : colors.textSecondary,
        }}
      >
        {t(`tours.luggage_${code}`)}
      </Text>
    </Pressable>
  )
}

function VehicleOption({
  vehicle,
  selected,
  disabled,
  onSelect,
}: {
  vehicle: TourVehicleOption
  selected: boolean
  disabled: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <Pressable
      onPress={onSelect}
      disabled={disabled}
      accessibilityRole="radio"
      accessibilityState={{ selected, checked: selected, disabled }}
      accessibilityLabel={`${vehicle.label}, ${t('tours.capacity', { count: vehicle.seats })}`}
      testID={`tours.vehicle-${vehicle.unitId}`}
      style={{
        minHeight: INTERACTION.minTouchTarget,
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.sm,
        borderRadius: RADIUS.lg,
        borderWidth: 1,
        borderColor: selected ? colors.textAccent : colors.border,
        backgroundColor: selected ? colors.surfaceSubtle : colors.surface,
        opacity: disabled ? 0.45 : 1,
        marginBottom: SPACING.sm,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 15, color: colors.textPrimary }}>
            {vehicle.label}
          </Text>
          <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}>
            {t('tours.capacity', { count: vehicle.seats })}
          </Text>
        </View>
        {selected ? <Check size={20} color={colors.textAccent} strokeWidth={2.2} /> : null}
      </View>
    </Pressable>
  )
}

function MetaChip({ icon, label }: { icon: ReactNode; label: string }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: SPACING.sm + 2,
        paddingVertical: SPACING.xs,
        borderRadius: RADIUS.full,
        backgroundColor: colors.surfaceSubtle,
      }}
    >
      {icon}
      <Text
        style={{ marginLeft: 4, fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}
      >
        {label}
      </Text>
    </View>
  )
}

function PrivateRoutePlace({
  label,
  place,
  color,
}: {
  label: string
  place: string
  color: string
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
      <View style={{ width: 14, alignItems: 'center' }}>
        <View
          style={{
            width: 8,
            height: 8,
            borderRadius: RADIUS.full,
            backgroundColor: color,
          }}
        />
      </View>
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}>
          {label}
        </Text>
        <Text
          numberOfLines={1}
          style={{
            marginTop: 1,
            fontFamily: FONTS.bodyBold,
            fontSize: 14,
            color: colors.textPrimary,
          }}
        >
          {place}
        </Text>
      </View>
    </View>
  )
}

function TravelInfoCard({
  icon,
  title,
  children,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
}) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  return (
    <Card>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: SPACING.xs }}>
        <View
          style={{
            width: 28,
            height: 28,
            borderRadius: RADIUS.full,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: colors.surfaceSubtle,
            marginRight: SPACING.sm,
          }}
        >
          {icon}
        </View>
        <Text
          style={{ fontFamily: FONTS.bodyBold, fontSize: 15, color: colors.textPrimary }}
          testID={`tours.travel-info-${title}`}
        >
          {title}
        </Text>
      </View>
      {children}
    </Card>
  )
}

function BulletList({ items, marker }: { items: string[]; marker?: 'warn' }) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const markerColor = marker === 'warn' ? colors.warning : colors.textAccent
  return (
    <View>
      {items.map((item, index) => (
        <View
          key={`${index}-${item}`}
          style={{
            flexDirection: 'row',
            marginTop: index === 0 ? 0 : SPACING.xs,
            alignItems: 'flex-start',
          }}
        >
          {marker === 'warn' ? (
            <AlertTriangle
              size={13}
              color={markerColor}
              strokeWidth={1.8}
              style={{ marginTop: 3, marginRight: SPACING.sm }}
            />
          ) : (
            <Check
              size={13}
              color={markerColor}
              strokeWidth={2.2}
              style={{ marginTop: 3, marginRight: SPACING.sm }}
            />
          )}
          <Text
            style={{
              flex: 1,
              fontFamily: FONTS.body,
              fontSize: 14,
              lineHeight: 20,
              color: colors.textSecondary,
            }}
          >
            {item}
          </Text>
        </View>
      ))}
    </View>
  )
}
